import { getDurationStringMilliseconds, isDurationString, useFeatureFlags } from '@chronosphereio/core';
import { useChartsTheme } from '@/components/context/ChartsProvider';
import { DATA_QUERY_KEY, EventDefinition, TimeSeriesQueryDefinition, UnknownSpec, VariableStateMap } from '@/core';
import { DataQueryPlugin, QueryPluginContext } from '../model';
import { useVizFrameworkConfig } from '../model/config';
import { EventQueryPlugin } from '../model/events';
import { useAppMetadataContext } from './AppMetadataProvider';
import { useCompareModeDurationInMs } from './compare-mode';
import { DataQuery, getDependsOnFunction } from './DataQueryPlugins';
import { UseDataQueryOptions } from './data-querying';
import { useGlobalFilters, useGlobalGroupByLabels } from './GlobalFilters';
import { useTimeRange } from './TimeRangeProviderV2';
import { useTemplateVariableValues } from './template-variables';

/**
 * Build plugin query context object from data available at runtime
 */
export function usePluginQueryContext(ctx?: UseDataQueryOptions): QueryPluginContext {
  const config = useVizFrameworkConfig();
  const { absoluteTimeRange, refreshKey, refreshIntervalInMs } = useTimeRange();
  const variableState = useTemplateVariableValues();
  const appMetadata = useAppMetadataContext();
  const compareDurationInMs = useCompareModeDurationInMs();
  const chartsThemeCtx = useChartsTheme();
  const { theme, ...chartsTheme } = chartsThemeCtx;
  const globalFilters = useGlobalFilters();
  const globalGroupByLabels = useGlobalGroupByLabels();
  const featureFlags = useFeatureFlags();

  return {
    ...ctx,
    stepMs:
      ctx?.resolution && isDurationString(ctx.resolution) ? getDurationStringMilliseconds(ctx.resolution) : undefined,
    timeRange: absoluteTimeRange,
    variableState,
    refreshKey,
    refreshIntervalInMs: refreshIntervalInMs,
    appMetadata,
    compareDurationInMs,
    theme,
    chartsTheme,
    globalFilters,
    globalGroupByLabels,
    config,
    featureFlags,
  };
}

/**
 * Returns a serialized string of the current state of variable values.
 */
function getVariableValuesKey(v: VariableStateMap) {
  return Object.values(v)
    .map((v) => JSON.stringify(v.value))
    .join(',');
}

/**
 * Filter the variable state map to only include the variables specified in `names`.
 */
function filterVariableStateMap(v: VariableStateMap, names?: string[]) {
  if (!names) {
    return v;
  }
  return Object.fromEntries(Object.entries(v).filter(([name]) => names.includes(name)));
}

/**
 * Returns the query key and query enabled state for a given query definition.
 */
export function getQueryOptions({
  plugin,
  definition,
  context,
  cursor,
  id,
}: {
  plugin?: DataQueryPlugin | DataQuery | EventQueryPlugin;
  definition: TimeSeriesQueryDefinition | EventDefinition;
  context: QueryPluginContext;
  cursor?: string;
  id?: string;
}) {
  const {
    timeRange,
    suggestedStepMs,
    variableState,
    refreshKey,
    compareDurationInMs,
    globalFilters,
    disabledGlobalFilters,
    dataType,
    queryLimits,
  } = context;

  const dependsOnFn = plugin ? getDependsOnFunction(plugin) : undefined;
  const dependencies = dependsOnFn ? dependsOnFn(definition.spec.plugin.spec as UnknownSpec, context) : {};
  const variableDependencies = dependencies?.variables;

  // Used for query truncation opt out
  const queryLimitsKey = queryLimits?.enabled;

  // Determine queryKey
  const filteredVariabledState = filterVariableStateMap(variableState, variableDependencies);
  const variablesValueKey = getVariableValuesKey(filteredVariabledState);
  const queryKey = [
    DATA_QUERY_KEY,
    // id is intentionally after DATA_QUERY_KEY, so we can easily target cancelling/resetting/etc. all the queries for
    // a given data query provider without needing to keep track of a list of the full query keys.
    id,
    definition,
    timeRange,
    suggestedStepMs,
    variablesValueKey,
    refreshKey,
    compareDurationInMs,
    globalFilters,
    disabledGlobalFilters,
    dataType,
    cursor,
    queryLimitsKey,
  ] as const;

  // Determine queryEnabled
  let waitToLoad = false;
  if (variableDependencies) {
    waitToLoad = variableDependencies.some((v) => {
      return shouldWaitForVariable(v, variableState);
    });
  }

  const queryEnabled = plugin !== undefined && !waitToLoad;

  return {
    queryKey,
    queryEnabled,
  };
}

/**
 * If a variable hasn’t loaded but has a default value that’s not null or an empty array, it’s safe to use.
 * Empty array means that the default is “all”, so we still need to wait to load.
 */
export const shouldWaitForVariable = (v: string, variableState: VariableStateMap) => {
  const isEmptyArray = Array.isArray(variableState[v]?.value) && (variableState[v]?.value as []).length === 0;
  if (
    variableState[v]?.hasLoaded === false &&
    variableState[v]?.loading === true &&
    variableState[v]?.value !== null &&
    !isEmptyArray
  ) {
    return false;
  }

  return variableState[v]?.loading;
};
